import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { Package, ArrowRight, Loader2, Search, User, MapPin, Shield, CreditCard, Users, MessageCircle, CheckCircle2, Home, Building2, AlertCircle } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { europeBranches as defEU, sriLankaBranches as defSL, heroImages } from '../mock/data';
import { COUNTRIES, DEFAULT_INSURANCE_PLANS, DEFAULT_DESKS } from '../mock/countries';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CountryPicker = ({ value, onChange, placeholder }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const selected = COUNTRIES.find((c) => c.code === value);
  const filtered = useMemo(() => COUNTRIES.filter((c) => c.name.toLowerCase().includes(q.toLowerCase())), [q]);
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(!open)} className="w-full h-12 border border-neutral-300 hover:border-black focus:border-[#c8102e] px-4 flex items-center gap-3 text-left bg-white transition-colors">
        {selected ? (<><span className="text-xl">{selected.flag}</span><span className="text-sm font-medium">{selected.name}</span></>) : (<span className="text-sm text-neutral-400">{placeholder}</span>)}
      </button>
      {open && (
        <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-neutral-200 shadow-xl max-h-80 overflow-hidden flex flex-col">
          <div className="p-3 border-b border-neutral-100">
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search country..." className="pl-9 h-10 text-sm" autoFocus />
            </div>
          </div>
          <div className="overflow-y-auto">
            {filtered.map((c) => (
              <button key={c.code} type="button" onClick={() => { onChange(c.code); setOpen(false); setQ(''); }} className="w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-neutral-50">
                <span className="text-xl">{c.flag}</span> {c.name}
              </button>
            ))}
            {filtered.length === 0 && <div className="p-4 text-sm text-neutral-400 text-center">No match</div>}
          </div>
        </div>
      )}
    </div>
  );
};

const Field = ({ label, required, hint, children, className }) => (
  <div className={className}>
    <label className="block text-[11px] tracking-[0.2em] uppercase text-neutral-600 font-semibold mb-2">{label}{required && <span className="text-[#c8102e]">*</span>}</label>
    {children}
    {hint && <p className="text-xs text-neutral-400 mt-1.5">{hint}</p>}
  </div>
);

const Section = ({ eyebrow, title, icon: Icon, children }) => (
  <div className="bg-white border border-neutral-200 p-6 md:p-8 mb-6">
    <div className="flex items-center gap-3 mb-1">
      <div className="w-9 h-9 bg-black text-white flex items-center justify-center"><Icon className="w-4 h-4" /></div>
      <div>
        <div className="text-[10px] tracking-[0.25em] uppercase text-[#c8102e] font-semibold">{eyebrow}</div>
        <h2 className="font-serif-display text-2xl font-bold text-black leading-none mt-1">{title}</h2>
      </div>
    </div>
    <div className="mt-6">{children}</div>
  </div>
);

const buildWhatsAppMessage = (f, insurance, desk) => {
  const from = COUNTRIES.find((c) => c.code === f.from_country);
  const to = COUNTRIES.find((c) => c.code === f.to_country);
  const dim = [f.length_cm, f.width_cm, f.height_cm].filter(Boolean).join(' x ');
  const lines = [
    `📦 *New Lanka Express parcel request*`,
    ``,
    `*Route:* ${from?.flag || ''} ${from?.name || f.from_country} → ${to?.flag || ''} ${to?.name || f.to_country}`,
    f.purpose ? `*Purpose:* ${f.purpose}` : '',
    ``,
    `*Parcel*`,
    `• Weight: ${f.weight_kg} kg`,
    dim ? `• Dimensions: ${dim} cm` : '',
    f.item_value_eur ? `• Declared value: €${f.item_value_eur}` : '',
    f.contents ? `• Contents: ${f.contents}` : '',
    ``,
    `*Sender*`,
    `• Name: ${f.sender_name}`,
    f.sender_id ? `• ID: ${f.sender_id}` : '',
    `• Phone: ${f.sender_phone}`,
    `• Address: ${f.sender_address}${f.sender_city ? ', ' + f.sender_city : ''}${f.sender_zip ? ' ' + f.sender_zip : ''}`,
    ``,
    `*Receiver*`,
    `• Name: ${f.receiver_name}`,
    f.receiver_id ? `• ID: ${f.receiver_id}` : '',
    `• Phone: ${f.receiver_phone}`,
    `• Address: ${f.receiver_address}${f.receiver_city ? ', ' + f.receiver_city : ''}${f.receiver_zip ? ' ' + f.receiver_zip : ''}`,
    ``,
    `*Delivery:* ${f.delivery_method === 'branch' ? '🏢 Drop-off at ' + (f.branch_name || 'branch') : '🏠 Home pickup'}`,
    `*Insurance:* ${insurance?.name || f.insurance_plan} (${insurance?.cover_label || ''}${insurance?.price_eur ? ' • €' + insurance.price_eur : ''})`,
    `*Payment:* Bank transfer (please share details)`,
    ``,
    `Sent via lankaexpress.eu send form`,
  ].filter(Boolean);
  return lines.join('\n');
};

const SendPackage = () => {
  const { getContent } = useSiteSettings();
  const branchesEU = getContent('branches_eu', defEU);
  const branchesSL = getContent('branches_sl', defSL);
  const allBranches = [...branchesEU, ...branchesSL];
  const insurancePlans = getContent('insurance_plans', DEFAULT_INSURANCE_PLANS);
  const desks = getContent('desks', DEFAULT_DESKS);

  const [f, setF] = useState({
    from_country: 'IT', to_country: 'LK', purpose: '',
    weight_kg: '', length_cm: '', width_cm: '', height_cm: '', contents: '', item_value_eur: '',
    sender_name: '', sender_id: '', sender_phone: '', sender_address: '', sender_city: '', sender_zip: '',
    receiver_name: '', receiver_id: '', receiver_phone: '', receiver_address: '', receiver_city: '', receiver_zip: '',
    delivery_method: 'home_pickup', branch_name: '',
    insurance_plan: 'basic', desk_id: 'auto',
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [err, setErr] = useState('');
  const set = (k, v) => setF({ ...f, [k]: v });

  const submit = async (e) => {
    e.preventDefault();
    setErr('');
    if (!f.from_country || !f.to_country) { setErr('Please select sender and receiver countries.'); return; }
    if (!f.weight_kg || parseFloat(f.weight_kg) <= 0) { setErr('Please enter a valid weight.'); return; }
    setBusy(true);
    try {
      const payload = { ...f, weight_kg: parseFloat(f.weight_kg) };
      ['length_cm', 'width_cm', 'height_cm', 'item_value_eur'].forEach((k) => { payload[k] = payload[k] === '' ? null : parseFloat(payload[k]); });
      const res = await axios.post(`${API}/packages`, payload);
      const insurance = insurancePlans.find((p) => p.id === f.insurance_plan);
      let desk = res.data.desk;
      if (!desk) desk = f.desk_id === 'auto' ? desks[0] : desks.find((d) => d.id === f.desk_id);
      const msg = buildWhatsAppMessage(payload, insurance, desk);
      const waNumber = (desk?.whatsapp || '').replace(/[^0-9]/g, '');
      const waUrl = `https://wa.me/${waNumber}?text=${encodeURIComponent(msg)}`;
      setDone({ waUrl, desk, insurance });
      window.open(waUrl, '_blank');
    } catch (e2) {
      setErr(e2?.response?.data?.detail || 'Something went wrong. Please try again.');
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="min-h-[calc(100vh-200px)] flex items-center justify-center bg-neutral-50 py-16">
        <div className="max-w-lg w-full mx-6 bg-white p-10 border border-neutral-200 text-center">
          <div className="w-16 h-16 rounded-full bg-[#c8102e] flex items-center justify-center mx-auto mb-5"><CheckCircle2 className="w-7 h-7 text-white" /></div>
          <div className="flex items-center gap-3 justify-center mb-3"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow">Sent</span></div>
          <h1 className="font-serif-display text-3xl font-bold text-black mb-3">Your request is on the way</h1>
          <p className="text-sm text-neutral-600 mb-6">We've received your parcel details and opened WhatsApp with our <span className="font-semibold text-black">{done.desk?.name}</span> team. If it didn't open automatically, tap the button below.</p>
          <a href={done.waUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:brightness-110 text-white h-12 px-6 font-semibold tracking-wide"><MessageCircle className="w-4 h-4" /> Open WhatsApp chat</a>
          <div className="mt-8 pt-6 border-t border-neutral-100 text-xs text-neutral-500">
            <div className="mb-1">Assigned desk</div>
            <div className="font-serif-display text-lg text-black">{done.desk?.name}</div>
            <div>{done.desk?.whatsapp}</div>
          </div>
          <button onClick={() => setDone(null)} className="mt-6 text-sm text-neutral-500 hover:text-[#c8102e]">Send another parcel</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <section className="relative h-[340px] flex items-center bg-black overflow-hidden">
        <img src={heroImages.truck} alt="Send parcel" className="absolute inset-0 w-full h-full object-cover opacity-45" />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/70 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <div className="flex items-center gap-3 mb-4"><span className="h-px w-10 bg-[#c8102e]" /><span className="eyebrow text-white">Send a Parcel</span></div>
          <h1 className="text-white font-serif-display text-5xl md:text-6xl font-bold leading-[1.05] max-w-3xl">Ship from any country to any country</h1>
          <p className="text-white/75 mt-4 max-w-2xl">Pick where you're sending from and to, add the details, and we'll save it for our team and open a pre-filled WhatsApp chat for your quote and bank-transfer payment.</p>
        </div>
      </section>

      <form onSubmit={submit} className="py-14 bg-neutral-50">
        <div className="max-w-4xl mx-auto px-6">
          <Section eyebrow="Where to & from" title="Route your shipment" icon={MapPin}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Sender's Country" required><CountryPicker value={f.from_country} onChange={(v) => set('from_country', v)} placeholder="Search sending country..." /></Field>
              <Field label="Receiver's Country" required><CountryPicker value={f.to_country} onChange={(v) => set('to_country', v)} placeholder="Search destination country..." /></Field>
              <Field label="Who is the parcel for? (relationship / purpose)" className="md:col-span-2"><Input value={f.purpose} onChange={(e) => set('purpose', e.target.value)} placeholder="e.g. gifts for family, personal effects" className="h-12" /></Field>
            </div>
          </Section>

          <Section eyebrow="Parcel details" title="What are you sending?" icon={Package}>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Field label="Weight (kg)" required><Input type="number" step="0.1" value={f.weight_kg} onChange={(e) => set('weight_kg', e.target.value)} className="h-12" /></Field>
              <Field label="Length (cm)"><Input type="number" value={f.length_cm} onChange={(e) => set('length_cm', e.target.value)} className="h-12" /></Field>
              <Field label="Width (cm)"><Input type="number" value={f.width_cm} onChange={(e) => set('width_cm', e.target.value)} className="h-12" /></Field>
              <Field label="Height (cm)"><Input type="number" value={f.height_cm} onChange={(e) => set('height_cm', e.target.value)} className="h-12" /></Field>
            </div>
            <Field label="What's inside?" className="mt-4"><Textarea value={f.contents} onChange={(e) => set('contents', e.target.value)} rows={2} placeholder="e.g. clothes, food items, gifts, documents" /></Field>
            <Field label="Declared item value (€)" hint="Approximate value of the contents in Euros. Used for insurance and customs." className="mt-4">
              <Input type="number" step="0.01" min="0" value={f.item_value_eur} onChange={(e) => set('item_value_eur', e.target.value)} placeholder="e.g. 150" className="h-12 max-w-xs" />
            </Field>
          </Section>

          <Section eyebrow="Sender details" title="Who is sending?" icon={User}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full name" required><Input value={f.sender_name} onChange={(e) => set('sender_name', e.target.value)} className="h-12" required /></Field>
              <Field label="ID / Passport number"><Input value={f.sender_id} onChange={(e) => set('sender_id', e.target.value)} className="h-12" /></Field>
              <Field label="Phone number" required><Input value={f.sender_phone} onChange={(e) => set('sender_phone', e.target.value)} className="h-12" required /></Field>
              <Field label="City"><Input value={f.sender_city} onChange={(e) => set('sender_city', e.target.value)} className="h-12" /></Field>
              <Field label="Pickup address" required className="md:col-span-2"><Input value={f.sender_address} onChange={(e) => set('sender_address', e.target.value)} className="h-12" required /></Field>
              <Field label="Postal / ZIP code"><Input value={f.sender_zip} onChange={(e) => set('sender_zip', e.target.value)} className="h-12" /></Field>
            </div>
          </Section>

          <Section eyebrow="Receiver details" title="Who is receiving?" icon={User}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Full name" required><Input value={f.receiver_name} onChange={(e) => set('receiver_name', e.target.value)} className="h-12" required /></Field>
              <Field label="ID / Passport number"><Input value={f.receiver_id} onChange={(e) => set('receiver_id', e.target.value)} className="h-12" /></Field>
              <Field label="Phone number" required><Input value={f.receiver_phone} onChange={(e) => set('receiver_phone', e.target.value)} className="h-12" required /></Field>
              <Field label="City"><Input value={f.receiver_city} onChange={(e) => set('receiver_city', e.target.value)} className="h-12" /></Field>
              <Field label="Delivery address" required className="md:col-span-2"><Input value={f.receiver_address} onChange={(e) => set('receiver_address', e.target.value)} className="h-12" required /></Field>
              <Field label="Postal / ZIP code"><Input value={f.receiver_zip} onChange={(e) => set('receiver_zip', e.target.value)} className="h-12" /></Field>
            </div>
          </Section>

          <Section eyebrow="Drop-off / Pick-up" title="How should we collect it?" icon={Home}>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
              {[
                { v: 'home_pickup', label: 'Home Pick-Up', desc: 'We collect from your address', icon: Home },
                { v: 'branch', label: 'Drop at a Branch', desc: 'Bring it to a Lanka Express branch', icon: Building2 },
              ].map((opt) => {
                const active = f.delivery_method === opt.v;
                const Icon = opt.icon;
                return (
                  <button key={opt.v} type="button" onClick={() => set('delivery_method', opt.v)} className={`p-5 text-left border-2 transition-all ${active ? 'border-black bg-neutral-50' : 'border-neutral-200 hover:border-neutral-400'}`}>
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5 text-[#c8102e]" />
                      <div>
                        <div className="font-serif-display text-lg font-bold text-black flex items-center gap-2">{opt.label} {active && <CheckCircle2 className="w-4 h-4 text-[#c8102e]" />}</div>
                        <div className="text-xs text-neutral-500">{opt.desc}</div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
            {f.delivery_method === 'branch' && (
              <Field label="Choose a branch" required>
                <select value={f.branch_name} onChange={(e) => set('branch_name', e.target.value)} required className="w-full h-12 border border-neutral-300 px-3 bg-white text-sm">
                  <option value="">— Select a branch —</option>
                  <optgroup label="Europe">
                    {branchesEU.map((b) => <option key={b.name} value={b.name}>{b.name} — {b.city || ''} {b.country || ''}</option>)}
                  </optgroup>
                  <optgroup label="Sri Lanka">
                    {branchesSL.map((b) => <option key={b.name} value={b.name}>{b.name}</option>)}
                  </optgroup>
                </select>
              </Field>
            )}
          </Section>

          <Section eyebrow="Insurance cover" title="Protect your parcel" icon={Shield}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {insurancePlans.map((p) => {
                const active = f.insurance_plan === p.id;
                return (
                  <button key={p.id} type="button" onClick={() => set('insurance_plan', p.id)} className={`p-5 text-left border-2 transition-all ${active ? 'border-black bg-neutral-50' : 'border-neutral-200 hover:border-neutral-400'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-serif-display text-xl font-bold text-black">{p.name}</span>
                      <span className={`text-sm font-bold ${p.price_eur ? 'text-[#c8102e]' : 'text-emerald-600'}`}>{p.price_eur ? `€${p.price_eur}` : 'Free'}</span>
                    </div>
                    <div className="text-xs uppercase tracking-[0.15em] text-neutral-500 font-semibold mb-2">{p.cover_label}</div>
                    <p className="text-xs text-neutral-600 leading-relaxed">{p.desc}</p>
                    {active && <div className="mt-3 inline-flex items-center gap-1 text-[10px] font-semibold text-[#c8102e]"><CheckCircle2 className="w-3 h-3" /> Selected</div>}
                  </button>
                );
              })}
            </div>
          </Section>

          <Section eyebrow="Payment by bank transfer" title="Bank transfer only" icon={CreditCard}>
            <p className="text-sm text-neutral-600 leading-relaxed">Once you submit, our team shares the bank-transfer details over WhatsApp to confirm your booking. You will not be charged on this website.</p>
          </Section>

          <Section eyebrow="Send your sheet to" title="Which team should handle it?" icon={Users}>
            <p className="text-sm text-neutral-500 mb-4">Choose which team you'd like to reach, or keep "Auto" and we'll route it to the right desk for your route.</p>
            <div className="space-y-2">
              <button type="button" onClick={() => set('desk_id', 'auto')} className={`w-full flex items-center justify-between p-4 border-2 text-left transition-all ${f.desk_id === 'auto' ? 'border-black bg-neutral-50' : 'border-neutral-200 hover:border-neutral-400'}`}>
                <div>
                  <div className="font-serif-display font-bold text-black">Auto (recommended)</div>
                  <div className="text-xs text-neutral-500">Routed to the right desk</div>
                </div>
                {f.desk_id === 'auto' && <CheckCircle2 className="w-5 h-5 text-[#c8102e]" />}
              </button>
              {desks.map((d) => (
                <button key={d.id} type="button" onClick={() => set('desk_id', d.id)} className={`w-full flex items-center justify-between p-4 border-2 text-left transition-all ${f.desk_id === d.id ? 'border-black bg-neutral-50' : 'border-neutral-200 hover:border-neutral-400'}`}>
                  <div>
                    <div className="font-serif-display font-bold text-black">{d.name}</div>
                    <div className="text-xs text-neutral-500">{d.whatsapp}</div>
                  </div>
                  {f.desk_id === d.id && <CheckCircle2 className="w-5 h-5 text-[#c8102e]" />}
                </button>
              ))}
            </div>
            <p className="text-xs text-neutral-400 mt-4">Your details are shared with our team and sent to the WhatsApp line handling your route.</p>
          </Section>

          {err && <div className="mb-4 flex items-center gap-2 text-sm text-[#c8102e] bg-red-50 border border-red-200 p-3"><AlertCircle className="w-4 h-4" /> {err}</div>}

          <Button type="submit" disabled={busy} className="w-full bg-[#c8102e] hover:bg-black text-white h-14 font-semibold tracking-[0.18em] uppercase text-[13px]">
            {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <MessageCircle className="w-4 h-4 mr-2" />}
            Submit & Open WhatsApp
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SendPackage;
